
import React from 'react';
import { Link } from 'react-router-dom';
import { Calendar, Clock, Tag } from 'lucide-react';
import { TutorialMetadata } from '@/utils/sample-data';
import { Badge } from '@/components/ui/badge';
import { Card, CardContent, CardFooter, CardHeader } from '@/components/ui/card';

interface TutorialCardProps {
  tutorial: TutorialMetadata;
}

export function TutorialCard({ tutorial }: TutorialCardProps) {
  const totalReadingTime = tutorial.chapters.reduce((sum, chapter) => sum + chapter.readingTime, 0);
  
  return (
    <Link to={`/tutorials/${tutorial.id}`}>
      <Card className="h-full overflow-hidden transition-all hover:shadow-md">
        <div className="aspect-video w-full overflow-hidden bg-muted">
          {tutorial.featuredImage ? (
            <img 
              src={tutorial.featuredImage} 
              alt={tutorial.title}
              className="w-full h-full object-cover transition-transform duration-300 hover:scale-105"
              loading="lazy"
            />
          ) : (
            <div className="w-full h-full flex items-center justify-center bg-muted">
              <span className="text-muted-foreground">No image</span>
            </div>
          )}
        </div>
        <CardHeader className="pb-2">
          <div className="flex justify-between items-start gap-2">
            <Badge variant="outline" className="text-xs capitalize">
              {tutorial.difficulty}
            </Badge>
            <div className="flex items-center text-xs text-muted-foreground">
              <Calendar className="h-3 w-3 mr-1" />
              <span>{new Date(tutorial.lastUpdated).toLocaleDateString()}</span>
            </div>
          </div>
          <h3 className="text-lg font-semibold mt-1 line-clamp-2">{tutorial.title}</h3>
        </CardHeader>
        <CardContent className="pb-4">
          <p className="text-sm text-muted-foreground line-clamp-3">{tutorial.description}</p>
        </CardContent>
        <CardFooter className="pt-0 flex justify-between items-center text-xs text-muted-foreground">
          <div className="flex items-center">
            <Clock className="h-3 w-3 mr-1" />
            <span>{tutorial.chapters.length} chapters • {totalReadingTime} mins</span>
          </div>
          <div className="flex items-center">
            <Tag className="h-3 w-3 mr-1" />
            <span className="truncate max-w-[100px]">{tutorial.tags.slice(0, 2).join(', ')}</span>
          </div>
        </CardFooter>
      </Card>
    </Link>
  );
}
